import { InboxEntry } from '@this+that/models';

export interface TaskTreeNode {
  entry: InboxEntry;
  depth: number;
  children: TaskTreeNode[];
}

const BEFORE = -1;
const AFTER = 1;
const EQUAL = 0;

// Codepoint order, not localeCompare: the server's index orders keys by bytes.
function compareCodepoints(a: string, b: string): number {
  if (a === b) {
    return EQUAL;
  }
  return a < b ? BEFORE : AFTER;
}

export function compareTaskTreeSiblings(a: InboxEntry, b: InboxEntry): number {
  if (
    a.childOrderKey &&
    b.childOrderKey &&
    a.childOrderKey !== b.childOrderKey
  ) {
    return compareCodepoints(a.childOrderKey, b.childOrderKey);
  }
  if (a.childOrderKey && !b.childOrderKey) {
    return BEFORE;
  }
  if (!a.childOrderKey && b.childOrderKey) {
    return AFTER;
  }
  const byCreatedAt = (a.createdAt ?? 0) - (b.createdAt ?? 0);
  return byCreatedAt === 0 ? compareCodepoints(a.id, b.id) : byCreatedAt;
}

function groupByParent(entries: readonly InboxEntry[]) {
  const byParent = new Map<string, InboxEntry[]>();
  for (const entry of entries) {
    if (entry.parentInboxEntryId) {
      const siblings = byParent.get(entry.parentInboxEntryId) ?? [];
      siblings.push(entry);
      byParent.set(entry.parentInboxEntryId, siblings);
    }
  }
  return byParent;
}

// Entries the root does not reach (a detached or orphaned child) are dropped.
export function buildTaskTree(
  entries: readonly InboxEntry[],
  rootId: string,
): TaskTreeNode | null {
  const root = entries.find((entry) => entry.id === rootId);
  if (!root) {
    return null;
  }
  const byParent = groupByParent(entries);
  const visited = new Set<string>();

  const build = (entry: InboxEntry, depth: number): TaskTreeNode => {
    visited.add(entry.id);
    const children = (byParent.get(entry.id) ?? [])
      .filter((child) => !visited.has(child.id))
      .sort(compareTaskTreeSiblings)
      .map((child) => build(child, depth + 1));
    return { entry, depth, children };
  };

  return build(root, 0);
}

export function findTaskTreeNode(
  node: TaskTreeNode,
  id: string,
): TaskTreeNode | null {
  if (node.entry.id === id) {
    return node;
  }
  for (const child of node.children) {
    const found = findTaskTreeNode(child, id);
    if (found) {
      return found;
    }
  }
  return null;
}

export function getTaskTreeHeight(node: TaskTreeNode): number {
  return node.children.reduce(
    (height, child) => Math.max(height, getTaskTreeHeight(child) + 1),
    0,
  );
}

export function countTaskTreeNodes(node: TaskTreeNode): number {
  return node.children.reduce(
    (count, child) => count + countTaskTreeNodes(child),
    1,
  );
}

export function flattenVisibleTaskTree(
  node: TaskTreeNode,
  collapsedIds: ReadonlySet<string>,
): TaskTreeNode[] {
  if (collapsedIds.has(node.entry.id)) {
    return [node];
  }
  return [
    node,
    ...node.children.flatMap((child) =>
      flattenVisibleTaskTree(child, collapsedIds),
    ),
  ];
}
